import type { Maybe } from '@snowit/icetypes';

type RandomIdParams = {
  length?: number;
  prefix?: string;
  suffix?: string;
  chars?: string;
  divider?: string;
};

/**
 * Generates an array of characters from their Unicode code points.
 * This includes all digits (0-9), lowercase letters (a-z), and uppercase letters (A-Z).
 * @returns An array of characters [0-9, a-z, A-Z].
 */
const getAllCharacters = (): string[] => {
  const digits = Array.from({ length: 10 }, (_, i) => String.fromCharCode(48 + i)); // '0' is 48 in Unicode
  const lowerCase = Array.from({ length: 26 }, (_, i) => String.fromCharCode(97 + i)); // 'a' is 97 in Unicode
  const upperCase = Array.from({ length: 26 }, (_, i) => String.fromCharCode(65 + i)); // 'A' is 65 in Unicode

  return [...digits, ...lowerCase, ...upperCase];
};

/**
 * Simple hash function to convert a string to a number.
 * @param input - The input string to hash.
 * @returns A hash number derived from the input.
 */
const hash = (input?: Maybe<string>): number => {
  if (!input) return 0;
  let hash = 0;
  for (let i = 0; i < input.length; i++) {
    const char = input.charCodeAt(i);
    hash = (hash * 31 + char) >>> 0; // Multiply by a prime and bitwise AND to keep within 32-bit range
  }
  return hash;
};

/**
 * Linear Congruential Generator (LCG) for pseudo-random number generation.
 * @param seed - A seed number to start the random number generation.
 * @returns A pseudo-random number between 0 and 1.
 */
const lcg = (seed: number): number => {
  const a = 1664525;
  const c = 1013904223;
  const m = 2 ** 32;
  return ((a * seed + c) % m) / m;
};

/**
 * Generates a "random" number based on the input, but always returns the same number for the same input.
 * @param input - The input string or number.
 * @returns A pseudo-random number between 0 and 1, consistent for the same input.
 */
const pseudo = (input?: Maybe<string | number>): number => {
  if (!input) return lcg(0);
  const seed = typeof input === 'number' ? input : hash(input.toString());
  return lcg(seed);
};

const id = (params: RandomIdParams = {}): string => {
  const { length = 16, divider = '_', prefix, suffix } = params;
  const chars: string[] = params.chars ? params.chars.split('') : getAllCharacters();

  let result = '';
  for (let i = 0; i < length; i++) {
    result += chars[Math.floor(Math.random() * chars.length)];
  }

  return [prefix, result, suffix].filter(Boolean).join(divider);
};

const number = (_min = 0, _max = 1, integer = false): number => {
  const phi = 0.618033988749895; // Φ
  const min = Math.min(_min, _max);
  const max = Math.max(_min, _max);
  if (min === max) return min;
  let rand = Math.random();
  rand += phi;
  rand %= 1;
  rand = rand * (max - min) + min;
  if (integer) return Math.floor(rand);
  return rand;
};

export default {
  hash,
  lcg,
  pseudo,
  id,
  number,
};
